import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Minus, Plus, ArrowRight, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import { useProducts } from "../context/ProductsContext";
import { useCart } from "../context/CartContext";

const PACK_PRICE = 49.99;
const PACK_SIZE = 6;

const HERO_IMG = "/hero-bottles-3d.png";

export default function SixPackBuilder() {
  const { add } = useCart();
  const { products } = useProducts();
  const [counts, setCounts] = useState({});

  const totalSelected = useMemo(
    () => Object.values(counts).reduce((s, n) => s + n, 0),
    [counts]
  );
  const remaining = PACK_SIZE - totalSelected;

  const inc = (id) => {
    if (totalSelected >= PACK_SIZE) return;
    setCounts((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  };
  const dec = (id) =>
    setCounts((c) => {
      const next = { ...c, [id]: Math.max(0, (c[id] || 0) - 1) };
      if (next[id] === 0) delete next[id];
      return next;
    });

  const oneOfEach = () => {
    // 5 different + 1 extra of first — user can adjust
    if (!products.length) return;
    const preset = {};
    products.forEach((p) => (preset[p.id] = 1));
    preset[products[0].id] = 2;
    setCounts(preset);
  };
  const clear = () => setCounts({});

  const composition = products
    .map((p) => ({ p, qty: counts[p.id] || 0 }))
    .filter((x) => x.qty > 0);

  const addPack = () => {
    if (totalSelected !== PACK_SIZE) return;
    const desc = composition
      .map(({ p, qty }) => `${qty}× ${p.name}`)
      .join(" · ");
    add(
      {
        id: `sixpack-${Date.now()}`,
        lookup_key: "sr_sixpack",
        name: "Six Pack",
        subtitle: desc,
        price: PACK_PRICE,
        image: HERO_IMG,
      },
      1
    );
    toast.success("Six Pack added to bag");
    setCounts({});
  };

  return (
    <section
      data-testid="sixpack-builder"
      className="relative bg-[color:var(--sr-secondary)] grain py-24 md:py-40 overflow-hidden"
    >
      <div className="sr-container">
        {/* Heading */}
        <div className="grid grid-cols-12 gap-6 mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.6 }}
              className="label-eyebrow text-[color:var(--sr-primary)] mb-6 flex items-center gap-3"
            >
              <span className="w-8 h-px bg-[color:var(--sr-accent)]" /> Six Pack · $49.99
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.35 }}
              transition={{ duration: 0.9 }}
              className="font-serif text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.92] tracking-tight text-[color:var(--sr-primary)]"
            >
              Build your six.
              <br />
              <em className="italic font-light text-[color:var(--sr-accent)]">
                Mix, match, or repeat.
              </em>
            </motion.h2>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="col-span-12 md:col-span-4 md:col-start-9 self-end text-[color:var(--sr-text)]/75 leading-relaxed"
          >
            Choose any combination of six bottles — one of each, six of your
            favourite, or something in between. One flat price.
          </motion.div>
        </div>

        {/* Builder */}
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          {/* Left — bottle picker (3 top, 2 bottom) */}
          <div className="col-span-12 lg:col-span-8">
            <ul className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {products.map((p) => {
                const qty = counts[p.id] || 0;
                const disabled = qty === 0 && totalSelected >= PACK_SIZE;
                return (
                  <li
                    key={p.id}
                    data-testid={`sixpack-tile-${p.id}`}
                    className={`relative bg-[color:var(--sr-bg)] p-3 md:p-4 border transition-colors ${
                      qty > 0
                        ? "border-[color:var(--sr-primary)]"
                        : "hairline hover:border-[color:var(--sr-primary)]/40"
                    }`}
                  >
                    <div className="relative aspect-[4/5] overflow-hidden bg-[color:var(--sr-secondary)]">
                      <img
                        src={p.image}
                        alt={p.name}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      {qty > 0 && (
                        <span
                          data-testid={`sixpack-badge-${p.id}`}
                          className="absolute top-3 right-3 min-w-7 h-7 px-2 inline-flex items-center justify-center rounded-full bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)] label-eyebrow"
                        >
                          {qty}
                        </span>
                      )}
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-2">
                      <p className="font-serif text-lg md:text-xl leading-tight text-[color:var(--sr-primary)]">
                        {p.name}
                      </p>
                    </div>
                    <div className="mt-3 flex items-center justify-between border-t hairline-light pt-3">
                      <button
                        type="button"
                        onClick={() => dec(p.id)}
                        data-testid={`sixpack-dec-${p.id}`}
                        disabled={qty === 0}
                        className="p-3 min-h-[44px] min-w-[44px] flex items-center justify-center border hairline text-[color:var(--sr-primary)] disabled:opacity-30 hover:border-[color:var(--sr-primary)] transition-colors"
                        aria-label={`Decrease ${p.name}`}
                      >
                        <Minus size={14} strokeWidth={1.5} />
                      </button>
                      <span
                        data-testid={`sixpack-qty-${p.id}`}
                        className="tabular-nums text-[color:var(--sr-primary)] font-serif text-lg"
                      >
                        {qty}
                      </span>
                      <button
                        type="button"
                        onClick={() => inc(p.id)}
                        data-testid={`sixpack-inc-${p.id}`}
                        disabled={disabled}
                        className="p-3 min-h-[44px] min-w-[44px] flex items-center justify-center border hairline text-[color:var(--sr-primary)] disabled:opacity-30 hover:border-[color:var(--sr-primary)] transition-colors"
                        aria-label={`Increase ${p.name}`}
                      >
                        <Plus size={14} strokeWidth={1.5} />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={oneOfEach}
                data-testid="sixpack-preset-oneofeach"
                className="label-eyebrow inline-flex items-center gap-2 px-4 py-3 min-h-[44px] border hairline text-[color:var(--sr-primary)] hover:border-[color:var(--sr-primary)] transition-colors"
              >
                <Sparkles size={14} strokeWidth={1.25} /> One of each + extra Golden
              </button>
              <button
                type="button"
                onClick={clear}
                data-testid="sixpack-reset"
                className="label-eyebrow px-4 py-3 min-h-[44px] text-[color:var(--sr-muted)] hover:text-[color:var(--sr-primary)] transition-colors"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Right — summary rail */}
          <aside className="col-span-12 lg:col-span-4">
            <div className="sticky top-28 bg-[color:var(--sr-bg)] p-6 md:p-8 border hairline">
              <p className="label-eyebrow text-[color:var(--sr-muted)]">
                Your six pack
              </p>
              <div className="mt-4 flex items-end justify-between gap-4">
                <p
                  data-testid="sixpack-count"
                  className="font-serif text-5xl md:text-6xl text-[color:var(--sr-primary)] leading-none tabular-nums"
                >
                  {totalSelected}
                  <span className="text-[color:var(--sr-muted)] text-3xl align-baseline">
                    /{PACK_SIZE}
                  </span>
                </p>
                <p className="font-serif text-2xl text-[color:var(--sr-primary)] tabular-nums">
                  $49.99
                </p>
              </div>

              {/* Progress */}
              <div className="mt-5 h-px w-full bg-[color:var(--sr-primary)]/10 relative overflow-hidden">
                <motion.div
                  className="absolute left-0 top-0 bottom-0 bg-[color:var(--sr-accent)]"
                  animate={{ width: `${(totalSelected / PACK_SIZE) * 100}%` }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>

              {/* Composition list */}
              <ul className="mt-6 space-y-2 min-h-[60px]" data-testid="sixpack-composition">
                <AnimatePresence initial={false}>
                  {composition.length === 0 && (
                    <motion.li
                      key="empty"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-sm text-[color:var(--sr-muted)]"
                    >
                      Choose bottles to build your pack.
                    </motion.li>
                  )}
                  {composition.map(({ p, qty }) => (
                    <motion.li
                      key={p.id}
                      initial={{ opacity: 0, x: 8 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -8 }}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="font-serif text-lg text-[color:var(--sr-primary)] leading-tight">
                        {p.name}
                      </span>
                      <span className="label-eyebrow text-[color:var(--sr-accent)]">
                        ×{qty}
                      </span>
                    </motion.li>
                  ))}
                </AnimatePresence>
              </ul>

              <div className="mt-6 border-t hairline pt-4">
                <p className="text-xs text-[color:var(--sr-muted)] leading-relaxed">
                  {remaining > 0
                    ? `Add ${remaining} more bottle${remaining === 1 ? "" : "s"} to complete your pack.`
                    : "Your pack is ready."}
                </p>
              </div>

              <button
                type="button"
                onClick={addPack}
                disabled={totalSelected !== PACK_SIZE}
                data-testid="sixpack-add"
                className="mt-6 group inline-flex items-center justify-between w-full px-6 py-4 bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)] label-eyebrow hover:bg-[color:var(--sr-primary-deep)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <span className="inline-flex items-center gap-2">
                  {totalSelected === PACK_SIZE && (
                    <Check size={14} strokeWidth={1.5} />
                  )}
                  Add pack — $49.99
                </span>
                <ArrowRight size={16} strokeWidth={1.25} />
              </button>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
